import {
  Box, Button, Checkbox, Container, Flashbar, Form, FormField, Header, Input, Modal,
  Select, SpaceBetween, Spinner, StatusIndicator, Table, Textarea,
} from "@cloudscape-design/components";
import { useEffect, useState, useCallback } from "react";
import { getApiKey, setApiKey, clearApiKey } from "./apiKey";
import { getAuthToken } from "./auth";
import { getAwsCreds, setAwsCreds, clearAwsCreds, getDefaultRegion, setDefaultRegion } from "./awsCredentials";
import { apiFetch } from "./apiFetch";
import { getDatadogCreds, setDatadogCreds, clearDatadogCreds, DATADOG_DEFAULT_SITE } from "./datadogCredentials";
import { getJiraCreds, setJiraCreds, clearJiraCreds } from "./jiraCredentials";
import { useDocumentTitle } from "./useDocumentTitle";

const DATADOG_SITE_OPTIONS = [
  { label: "datadoghq.com (US1)", value: "datadoghq.com" },
  { label: "datadoghq.eu (EU)", value: "datadoghq.eu" },
  { label: "us3.datadoghq.com (US3)", value: "us3.datadoghq.com" },
  { label: "us5.datadoghq.com (US5)", value: "us5.datadoghq.com" },
  { label: "ap1.datadoghq.com (AP1)", value: "ap1.datadoghq.com" },
];

interface Account { id: number; Alias: string; accountid: string; }

type TestStatus = 'none' | 'loading' | 'match' | 'mismatch' | 'error';

export default function SettingsPage({ ssoEnabled }: { ssoEnabled?: boolean }) {
  useDocumentTitle("Settings");
  const [apiKeyValue, setApiKeyValue] = useState(getApiKey() ?? "");
  const [region, setRegion] = useState(getDefaultRegion());
  const [flash, setFlash] = useState<{ type: "success" | "info" | "error"; content: string } | null>(null);

  const [accounts, setAccounts] = useState<Account[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountsError, setAccountsError] = useState<string | null>(null);
  // bump to force re-render of status column after save/clear
  const [credsVersion, setCredsVersion] = useState(0);
  const [testStatus, setTestStatus] = useState<Record<string, TestStatus>>({});

  const [editAlias, setEditAlias] = useState<string | null>(null);
  const [accessKeyId, setAccessKeyId] = useState("");
  const [secretAccessKey, setSecretAccessKey] = useState("");
  const [sessionToken, setSessionToken] = useState("");
  const [exportCreds, setExportCreds] = useState("");

  // Datadog credential (app-wide, sessionStorage) — see datadogCredentials.ts.
  const existingDatadog = getDatadogCreds();
  const [datadogToken, setDatadogToken] = useState(existingDatadog?.token ?? "");
  const [datadogSite, setDatadogSite] = useState(existingDatadog?.site ?? DATADOG_DEFAULT_SITE);

  // Jira Data Center credential (app-wide, sessionStorage) — see jiraCredentials.ts.
  const existingJira = getJiraCreds();
  const [jiraBaseUrl, setJiraBaseUrl] = useState(existingJira?.baseUrl ?? "");
  const [jiraToken, setJiraToken] = useState(existingJira?.token ?? "");
  const [jiraInsecure, setJiraInsecure] = useState(existingJira?.insecure ?? false);

  const apiKey = getApiKey();
  const authToken = getAuthToken();

  const fetchAccounts = useCallback(() => {
    if (!authToken) return;
    setAccountsLoading(true);
    setAccountsError(null);
    apiFetch(`/accounts`)
      .then(r => { if (!r.ok) throw new Error(`API error: ${r.status}`); return r.json(); })
      .then(json => setAccounts(json.data.map((i: any) => ({ id: i.id, Alias: i.Alias, accountid: i.accountid }))))
      .catch(e => setAccountsError(e.message))
      .finally(() => setAccountsLoading(false));
  }, [authToken]);

  useEffect(() => { fetchAccounts(); }, [fetchAccounts]);

  const openEdit = (alias: string) => {
    const existing = getAwsCreds(alias);
    setEditAlias(alias);
    setAccessKeyId(existing?.accessKeyId ?? "");
    setSecretAccessKey(existing?.secretAccessKey ?? "");
    setSessionToken(existing?.sessionToken ?? "");
    setExportCreds("");
  };

  const parseExportCreds = (text: string) => {
    const get = (key: string) => {
      const match = text.match(new RegExp(`(?:export\\s+)?${key}=["']?([^"'\\n]+)["']?`));
      return match?.[1]?.trim() ?? "";
    };
    return {
      accessKeyId: get("AWS_ACCESS_KEY_ID"),
      secretAccessKey: get("AWS_SECRET_ACCESS_KEY"),
      sessionToken: get("AWS_SESSION_TOKEN"),
    };
  };

  const handleCredsSave = () => {
    if (!editAlias) return;
    if (exportCreds.trim()) {
      const parsed = parseExportCreds(exportCreds);
      setAwsCreds(editAlias, parsed);
    } else {
      setAwsCreds(editAlias, { accessKeyId, secretAccessKey, sessionToken });
    }
    setEditAlias(null);
    setCredsVersion(v => v + 1);
    setFlash({ type: "success", content: `Credentials saved for "${editAlias}"` });
  };

  const handleCredsClear = () => {
    if (!editAlias) return;
    clearAwsCreds(editAlias);
    setAccessKeyId("");
    setSecretAccessKey("");
    setSessionToken("");
    setExportCreds("");
    setEditAlias(null);
    setCredsVersion(v => v + 1);
    setFlash({ type: "info", content: `Credentials cleared for "${editAlias}"` });
  };

  const handleDatadogSave = () => {
    setDatadogCreds({ token: datadogToken, site: datadogSite || DATADOG_DEFAULT_SITE });
    setFlash({ type: "success", content: "Datadog credentials saved" });
  };

  const handleDatadogClear = () => {
    clearDatadogCreds();
    setDatadogToken("");
    setDatadogSite(DATADOG_DEFAULT_SITE);
    setFlash({ type: "info", content: "Datadog credentials cleared" });
  };

  const handleJiraSave = () => {
    setJiraCreds({ baseUrl: jiraBaseUrl.trim(), token: jiraToken, insecure: jiraInsecure });
    setFlash({ type: "success", content: "Jira credentials saved" });
  };

  const handleJiraClear = () => {
    clearJiraCreds();
    setJiraBaseUrl("");
    setJiraToken("");
    setJiraInsecure(false);
    setFlash({ type: "info", content: "Jira credentials cleared" });
  };

  const testConnection = async (account: Account) => {
    const creds = getAwsCreds(account.Alias);
    if (!creds) return;
    setTestStatus(s => ({ ...s, [account.Alias]: 'loading' }));
    try {
      const res = await apiFetch(`/cost/test-credentials?accountId=${account.accountid}`, {
        headers: {
          'x-aws-access-key-id': creds.accessKeyId,
          'x-aws-secret-access-key': creds.secretAccessKey,
          'x-aws-session-token': creds.sessionToken,
        },
      });
      if (!res.ok) throw new Error();
      const json = await res.json();
      setTestStatus(s => ({ ...s, [account.Alias]: json.match ? 'match' : 'mismatch' }));
    } catch {
      setTestStatus(s => ({ ...s, [account.Alias]: 'error' }));
    }
  };

  return (
    <SpaceBetween size="l">
      {flash && (
        <Flashbar items={[{ type: flash.type, content: flash.content, dismissible: true, onDismiss: () => setFlash(null) }]} />
      )}

      {/* Section 1: API Key */}
      {!ssoEnabled && (
      <Container header={<Header variant="h2">API Key</Header>}>
        <Form actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={() => { clearApiKey(); setApiKeyValue(""); setFlash({ type: "info", content: "API key cleared" }); }}>Clear</Button>
            <Button variant="primary" onClick={() => { setApiKey(apiKeyValue); setFlash({ type: "success", content: "API key saved" }); }}>Save</Button>
          </SpaceBetween>
        }>
          <FormField label="API Key" description="Stored in session storage — cleared when you close the tab.">
            <Input type="password" value={apiKeyValue} onChange={({ detail }) => setApiKeyValue(detail.value)} />
          </FormField>
        </Form>
      </Container>
      )}

      {/* Section 2: Default AWS Region */}
      <Container header={<Header variant="h2">Default AWS Region</Header>}>
        <Form actions={
          <Button variant="primary" onClick={() => { setDefaultRegion(region); setFlash({ type: "success", content: "Default region saved" }); }}>Save</Button>
        }>
          <FormField label="Region" description="Used as the default region for AWS API calls (stored in local storage).">
            <Input value={region} onChange={({ detail }) => setRegion(detail.value)} placeholder="eu-west-1" />
          </FormField>
        </Form>
      </Container>

      {/* Section 3: AWS Credentials per Account */}
      <Container header={<Header variant="h2">AWS Credentials</Header>}>
        <Table
          loading={accountsLoading}
          items={accounts}
          columnDefinitions={[
            { id: "alias", header: "Alias", cell: i => i.Alias },
            { id: "accountid", header: "Account ID", cell: i => i.accountid },
            {
              id: "status", header: "Status",
              cell: i => {
                // credsVersion used to trigger re-evaluation
                void credsVersion;
                const configured = !!getAwsCreds(i.Alias);
                return configured
                  ? <StatusIndicator type="success">Configured</StatusIndicator>
                  : <StatusIndicator type="stopped">Not configured</StatusIndicator>;
              },
            },
            {
              id: "actions", header: "Actions",
              cell: i => (
                <SpaceBetween direction="horizontal" size="xs">
                  <Button onClick={() => openEdit(i.Alias)}>Edit</Button>
                  <Button disabled={!getAwsCreds(i.Alias)} onClick={() => testConnection(i)}>Test</Button>
                </SpaceBetween>
              ),
            },
            {
              id: "test", header: "Connection",
              cell: i => {
                void credsVersion;
                const status = testStatus[i.Alias] || 'none';
                if (status === 'loading') return <Spinner />;
                if (status === 'match') return <StatusIndicator type="success">Verified</StatusIndicator>;
                if (status === 'mismatch') return <StatusIndicator type="warning">Account mismatch</StatusIndicator>;
                if (status === 'error') return <StatusIndicator type="error">Failed</StatusIndicator>;
                return '—';
              },
            },
          ]}
          empty={
            <Box textAlign="center" color={accountsError ? "text-status-error" : "text-body-secondary"}>
              {accountsError ? `Error: ${accountsError}` : !apiKey ? "No API key configured." : "No accounts found"}
            </Box>
          }
        />
      </Container>

      {/* Section 4: Datadog — app-wide token used to fetch metrics via the API proxy. */}
      <Container header={<Header variant="h2">Datadog</Header>}>
        <Form actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleDatadogClear}>Clear</Button>
            <Button variant="primary" onClick={handleDatadogSave} disabled={!datadogToken.trim()}>Save</Button>
          </SpaceBetween>
        }>
          <SpaceBetween size="m">
            <FormField
              label="API token"
              description="Datadog token used to fetch metrics. Stored in session storage — cleared when you close the tab — and sent only to the API as a per-request header."
            >
              <Input type="password" value={datadogToken} onChange={({ detail }) => setDatadogToken(detail.value)} />
            </FormField>
            <FormField label="Site" description="Your Datadog site/region. Determines the Datadog API base URL.">
              <Select
                selectedOption={DATADOG_SITE_OPTIONS.find(o => o.value === datadogSite) ?? { label: datadogSite, value: datadogSite }}
                options={DATADOG_SITE_OPTIONS}
                onChange={({ detail }) => setDatadogSite(detail.selectedOption.value ?? DATADOG_DEFAULT_SITE)}
              />
            </FormField>
          </SpaceBetween>
        </Form>
      </Container>

      {/* Section 5: Jira — app-wide Data Center PAT used to search issues via the API proxy. */}
      <Container header={<Header variant="h2">Jira</Header>}>
        <Form actions={
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={handleJiraClear}>Clear</Button>
            <Button variant="primary" onClick={handleJiraSave} disabled={!jiraBaseUrl.trim() || !jiraToken.trim()}>Save</Button>
          </SpaceBetween>
        }>
          <SpaceBetween size="m">
            <FormField label="Base URL" description="Your Jira Data Center base URL.">
              <Input value={jiraBaseUrl} onChange={({ detail }) => setJiraBaseUrl(detail.value)} placeholder="https://jira.example.com" />
            </FormField>
            <FormField
              label="Personal Access Token"
              description="Jira Data Center PAT used to search issues. Stored in session storage — cleared when you close the tab — and sent only to the API as a per-request header."
            >
              <Input type="password" value={jiraToken} onChange={({ detail }) => setJiraToken(detail.value)} />
            </FormField>
            <Checkbox checked={jiraInsecure} onChange={({ detail }) => setJiraInsecure(detail.checked)}>
              Allow insecure TLS
            </Checkbox>
          </SpaceBetween>
        </Form>
      </Container>

      {/* Edit Credentials Modal */}
      <Modal
        visible={editAlias !== null}
        onDismiss={() => setEditAlias(null)}
        header={`AWS Credentials — ${editAlias}`}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={handleCredsClear}>Clear</Button>
              <Button variant="primary" onClick={handleCredsSave}>Save</Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <FormField
            label="Paste SSO credentials (export format)"
            description="Paste the export-format credentials from IAM Identity Center. If filled, this takes priority over the fields below."
          >
            <Textarea
              value={exportCreds}
              onChange={({ detail }) => setExportCreds(detail.value)}
              rows={4}
              placeholder={'export AWS_ACCESS_KEY_ID="..."\nexport AWS_SECRET_ACCESS_KEY="..."\nexport AWS_SESSION_TOKEN="..."'}
            />
          </FormField>
          <FormField label="Access Key ID">
            <Input value={accessKeyId} onChange={({ detail }) => setAccessKeyId(detail.value)} />
          </FormField>
          <FormField label="Secret Access Key">
            <Input type="password" value={secretAccessKey} onChange={({ detail }) => setSecretAccessKey(detail.value)} />
          </FormField>
          <FormField label="Session Token">
            <Input type="password" value={sessionToken} onChange={({ detail }) => setSessionToken(detail.value)} />
          </FormField>
        </SpaceBetween>
      </Modal>
    </SpaceBetween>
  );
}
